import { ChangeDetectorRef, Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ImportType } from "./import.component";
import { Bind } from "../../decorators/bind.decorator";
import { RowData } from "../s25-spreadsheet/types/sheet.js.types.expanded";
import { OrganizationService } from "../../services/organization.service";
import { OrganizationImportSaveResponseDto, ImportService, Organization } from "./import.service";
import { ScrollLockService } from "../../services/scroll.lock.service";
import { OrganizationSpreadsheetUtil } from "./util/organization/organization.spreadsheet.util";
import { OrganizationTypeService } from "../../services/master.definitions/organization.misc/organization.type.service";
import { ImportUtil } from "./util/import.util";
import { SpreadSheetService } from "../s25-spreadsheet/spreadsheet.service";
import { SearchCriteriaService } from "../../services/search/search-criteria/search.criteria.service";
import { ContactService } from "../../services/contact.service";
import { S25Util } from "../../util/s25-util";

@Component({
    selector: "s25-ng-import-organizations",
    template: `
        @if (init) {
            <div class="c-margin-bottom--single">
                Upload a spreadsheet with organization data and membership information.
                <br />
                Prime Contact (optional): Specify a contact ID or email address. If using an email, the contact must
                exist in the system.
            </div>
            <div class="c-margin-bottom--single">
                <label>
                    <input type="checkbox" [(ngModel)]="isStudentEngagement" />
                    Mark all organizations and contacts as Student Engagement
                </label>
            </div>

            <div class="c-margin-bottom--single c-margin-top--single">
                <button class="aw-button aw-button--outline" (click)="downloadTemplate()">
                    Download Excel Template
                </button>
            </div>
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule],
})
export class S25ImportOrganizationsComponent implements OnInit {
    @Input() type: ImportType;
    @Input() parentCd?: ChangeDetectorRef;

    constructor(private cd: ChangeDetectorRef) {}

    init = false;
    isStudentEngagement = false;
    catNameMap = new Map<string, number>();
    ratingNameMap = new Map<string, number>();

    @Bind
    async onCompleteOrganizations(rows: RowData[]) {
        try {
            this.type.errors = [];
            this.type.successMessage = "";

            if (!rows || !rows.length) {
                this.type.errors.push("No sheet data found");
                this.refresh();
                return;
            }

            // Extract headers from first row
            const cellNumToHeader: Map<number, string> = new Map<number, string>();
            if (rows && rows.length > 0 && rows[0]?.cells) {
                for (let c = 0; c < rows[0].cells.length; c++) {
                    const cell = rows[0].cells[c];
                    if (cell?.text) {
                        cellNumToHeader.set(c, cell.text);
                    }
                }
            }

            const orgNameSet = new Set<string>();
            const orgTypeNameSet = new Set<string>();
            const catNameSet = new Set<string>();
            const ratingNameSet = new Set<string>();
            const errorSet = new Set<string>();

            let { organizations } = OrganizationSpreadsheetUtil.rowsToOrganizations(
                orgNameSet,
                orgTypeNameSet,
                catNameSet,
                ratingNameSet,
                errorSet,
                rows,
            );

            organizations = organizations.filter((o) => !!o);

            // Collect prime contact emails for lookup
            const primeContactEmailSet = new Set<string>();
            for (const org of organizations) {
                if (org.primeContactEmail) {
                    primeContactEmailSet.add(org.primeContactEmail.trim().toLowerCase());
                }
            }
            if (!organizations || !organizations.length) {
                // Check for required organization columns (at least one of acctId or acctName)
                const orgColumns = ["organization_id", "acct_id", "organization_name", "acct_name", "name"];
                const hasOrgColumn = orgColumns.some((col) =>
                    Array.from(cellNumToHeader.values()).some(
                        (h) => ImportUtil.normalize(h) === ImportUtil.normalize(col),
                    ),
                );

                // Check for required membership columns
                const roleNameColumns = ["membership_role_name", "role_name"];
                const hasRoleName = roleNameColumns.some((col) =>
                    Array.from(cellNumToHeader.values()).some(
                        (h) => ImportUtil.normalize(h) === ImportUtil.normalize(col),
                    ),
                );

                const missing: string[] = [];
                if (!hasOrgColumn) {
                    missing.push("organization_name (or organization_id)");
                }
                if (!hasRoleName) {
                    missing.push("role_name");
                }

                if (missing.length > 0) {
                    this.type.errors.push(
                        `No valid organizations found. Missing required columns: ${missing.join(", ")}`,
                    );
                } else {
                    this.type.errors.push("No valid organizations found");
                }
                this.refresh();
                return;
            }

            // get organizations by name and form map from name -> id
            const orgNameMap = await OrganizationService.getOrganizationsByName(Array.from(orgNameSet)).then((orgs) => {
                return orgs.reduce((m, org) => {
                    if (org.acctName) {
                        org.acctName = org.acctName.trim();
                        m.set(org.acctName, org.acctId);
                    }
                    return m;
                }, new Map<string, number>());
            });

            // get organization types by name and form map from name -> id
            const orgTypeNameMap = await OrganizationTypeService.getOrgTypesByNames(Array.from(orgTypeNameSet)).then(
                (types) => {
                    return types.reduce((m, type) => {
                        if (type.orgType) {
                            type.orgType = type.orgType.trim();
                            m.set(type.orgType, type.orgTypeId);
                        }
                        return m;
                    }, new Map<string, number>());
                },
            );

            // get prime contacts by email and form map from email -> id
            let primeContactEmailMap = new Map<string, number>();
            if (primeContactEmailSet.size > 0) {
                primeContactEmailMap = await ContactService.getContactsByEmails(Array.from(primeContactEmailSet)).then(
                    (contacts) => {
                        return contacts.reduce((m, cont) => {
                            if (cont.email && cont.contId !== undefined) {
                                const normalizedEmail = cont.email.trim().toLowerCase();
                                m.set(normalizedEmail, cont.contId);
                            }
                            return m;
                        }, new Map<string, number>());
                    },
                );
            }

            // Resolve org type names to IDs and validate
            for (const org of organizations) {
                if (org.orgTypeName && !org.orgTypeId) {
                    const orgTypeId = orgTypeNameMap.get(org.orgTypeName);
                    if (orgTypeId) {
                        org.orgTypeId = orgTypeId;
                    } else {
                        errorSet.add(`Organization type not found: ${org.orgTypeName}`);
                    }
                }
                // Remove orgTypeName before sending to backend
                delete org.orgTypeName;

                // Resolve rating names to IDs using pre-fetched map
                if (org.ratingName && !org.acctRating) {
                    const ratingId = this.ratingNameMap.get(org.ratingName);
                    if (ratingId) {
                        org.acctRating = ratingId;
                    } else {
                        errorSet.add(`Organization rating not found: ${org.ratingName}`);
                    }
                }
                // Remove ratingName before sending to backend
                delete org.ratingName;

                // Resolve prime contact email to ID
                if (org.primeContactEmail && !org.primeContact) {
                    const normalizedEmail = org.primeContactEmail.trim().toLowerCase();
                    const contId = primeContactEmailMap.get(normalizedEmail);
                    if (contId) {
                        org.primeContact = contId;
                    } else {
                        errorSet.add(`Prime contact not found by email: ${org.primeContactEmail}`);
                    }
                }
                // Remove primeContactEmail before sending to backend
                delete org.primeContactEmail;

                // Resolve category names to IDs when possible, otherwise send names to backend
                if (org.categoryNames && org.categoryNames.length > 0) {
                    org.categories = org.categories || [];
                    for (const catName of org.categoryNames) {
                        const catId = this.catNameMap.get(catName);
                        if (catId) {
                            // Category found - send ID so backend can skip lookup/creation
                            if (!org.categories.find((cat) => cat.category_id === catId)) {
                                org.categories.push({ category_id: catId });
                            }
                        } else {
                            // Category not found - send name to backend for resolution/creation
                            if (!org.categories.find((cat) => cat.category_name === catName)) {
                                org.categories.push({ category_name: catName });
                            }
                        }
                    }
                    // Remove categoryNames before sending to backend
                    delete org.categoryNames;
                }

                this.validateFieldLength(errorSet, "Organization name", org.acctName, 40);
                this.validateFieldLength(errorSet, "Organization title", org.acctTitle, 80);
                if (org.addresses) {
                    for (const addr of org.addresses) {
                        this.validateFieldLength(errorSet, "Street address", addr.address, 150);
                        this.validateFieldLength(errorSet, "City", addr.city, 40);
                        this.validateFieldLength(errorSet, "State/Province", addr.stateProv, 40);
                        this.validateFieldLength(errorSet, "Zip/Postal Code", addr.zipPostCode, 20);
                        this.validateFieldLength(errorSet, "Country", addr.country, 40);
                        this.validateFieldLength(errorSet, "Phone", addr.phone, 24);
                        this.validateFieldLength(errorSet, "Fax", addr.fax, 24);
                    }
                }
            }

            // Validate required fields for memberships
            for (const org of organizations) {
                if (org.memberships) {
                    for (const membership of org.memberships) {
                        // acctId will be set from parent org after org is created/updated, but resolve acctName as fallback
                        if (!membership.acctId && membership.acctName) {
                            membership.acctId = orgNameMap.get(membership.acctName);
                        }
                        // Validate required fields
                        if (!membership.roleName) {
                            errorSet.add(`Membership missing required field: roleName`);
                        }

                        // Validate required contact fields for ALL memberships (both insert and update)
                        if (!membership.contactEmail || !membership.contactEmail.trim()) {
                            errorSet.add(`Membership missing required field: contactEmail`);
                        }
                        if (!membership.contactFirstName || !membership.contactFirstName.trim()) {
                            errorSet.add(`Membership missing required field: contactFirstName`);
                        }
                        if (!membership.contactFamilyName || !membership.contactFamilyName.trim()) {
                            errorSet.add(`Membership missing required field: contactFamilyName`);
                        }

                        this.validateFieldLength(errorSet, "Contact email", membership.contactEmail, 80);
                        this.validateFieldLength(errorSet, "Contact first name", membership.contactFirstName, 40);
                        this.validateFieldLength(errorSet, "Contact family name", membership.contactFamilyName, 40);
                    }
                }
            }

            // Validate contact info consistency per email (before API call)
            // Note: Same contact (email) can appear in multiple memberships with different roles, but contact info must be consistent
            const emailToFirstName = new Map<string, string>();
            const emailToFamilyName = new Map<string, string>();

            for (const org of organizations) {
                if (org.memberships) {
                    for (const membership of org.memberships) {
                        // Validate for ALL memberships (email, firstName, familyName already validated above)
                        if (membership.contactEmail && membership.contactFirstName && membership.contactFamilyName) {
                            const normalizedEmail = membership.contactEmail.trim().toLowerCase();
                            const firstName = membership.contactFirstName.trim();
                            const familyName = membership.contactFamilyName.trim();

                            // Check contact info consistency for this email
                            const existingFirstName = emailToFirstName.get(normalizedEmail);
                            const existingFamilyName = emailToFamilyName.get(normalizedEmail);

                            if (existingFirstName && existingFirstName !== firstName) {
                                errorSet.add(
                                    `Contact email "${membership.contactEmail}" has conflicting first names: "${existingFirstName}" and "${firstName}"`,
                                );
                            }
                            if (existingFamilyName && existingFamilyName !== familyName) {
                                errorSet.add(
                                    `Contact email "${membership.contactEmail}" has conflicting family names: "${existingFamilyName}" and "${familyName}"`,
                                );
                            }

                            // Store contact info for this email (use first value)
                            if (!existingFirstName) {
                                emailToFirstName.set(normalizedEmail, firstName);
                            }
                            if (!existingFamilyName) {
                                emailToFamilyName.set(normalizedEmail, familyName);
                            }
                        }
                    }
                }
            }

            // Check for validation errors
            if (errorSet.size) {
                this.type.errors = Array.from(errorSet);
                this.refresh();
                return;
            }

            OrganizationSpreadsheetUtil.collapseDuplicateMemberships(organizations);

            const response = await this.importOrganizations(organizations);
            if (response?.rowErrors?.length) {
                for (const rowError of response.rowErrors) {
                    this.type.errors.push(rowError);
                }
            }
            if (response?.error) {
                if (response.error === "no_organizations") {
                    this.type.errors.push("No organizations to import");
                } else {
                    this.type.errors.push("Error importing organizations: [" + response.error + "]");
                }
            } else if (!this.type.errors.length) {
                this.type.successMessage = "Organizations imported successfully";
            }

            this.refresh();
        } catch (ex) {
            const errorData = ex?.data || ex?.error;
            if (errorData?.error) {
                this.type.errors.push(errorData.error);
            } else {
                S25Util.showError(ex, "Failed to import organizations");
            }
            this.refresh();
        }
    }

    async importOrganizations(organizations: Organization[]): Promise<OrganizationImportSaveResponseDto> {
        this.type.errors = [];
        if (!organizations || !organizations.length) {
            this.type.errors.push("No valid organizations found");
            this.refresh();
            return { error: "no_organizations" };
        }

        return ImportService.importOrganizations({
            organizations: organizations,
            isStudentEngagement: this.isStudentEngagement,
        });
    }

    async ngOnInit() {
        this.type.onComplete = this.onCompleteOrganizations;

        // Fetch organization categories and create name-to-ID map
        this.catNameMap = await SearchCriteriaService.getOrganizationCategories().then(
            (categories: Array<{ itemName: string; itemId: number }>) => {
                return categories.reduce((map: Map<string, number>, cat) => {
                    if (cat.itemName) {
                        map.set(cat.itemName.trim(), cat.itemId);
                    }
                    return map;
                }, new Map<string, number>());
            },
        );

        // Fetch organization ratings and create name-to-ID map
        this.ratingNameMap = await SearchCriteriaService.getOrganizationRatings().then(
            (ratings: Array<{ itemName: string; itemId: number }>) => {
                return ratings.reduce((map: Map<string, number>, rating) => {
                    if (rating.itemName) {
                        map.set(rating.itemName.trim(), rating.itemId);
                    }
                    return map;
                }, new Map<string, number>());
            },
        );

        this.init = true;
        this.refresh();
    }

    refresh() {
        this.cd.detectChanges();
        this.parentCd?.detectChanges();
        ScrollLockService.unlockBodyScroll();
    }

    private validateFieldLength(
        errorSet: Set<string>,
        fieldName: string,
        value: string | undefined | null,
        maxLength: number,
    ): void {
        if (value != null && value.length > maxLength) {
            errorSet.add(
                `${fieldName} cannot exceed ${maxLength} characters: "${value.substring(0, Math.min(maxLength + 10, value.length))}${value.length > maxLength + 10 ? "..." : ""}"`,
            );
        }
    }

    async downloadTemplate() {
        const templateData = [
            {
                // Organization identification (use either ID or Name)
                "Organization ID": "",
                "Organization Name": "Sample Organization",
                Title: "Sample Organization Title",
                "Account Number": "ORG-001",
                Type: "Student Organization",
                Category: "Category1|Category2",
                Rating: "Good",
                "S25 Name": "",
                // Administrative Address (addrType = 1)
                "Administrative Street Address": "123 Main St",
                "Administrative City": "Springfield",
                "Administrative State / Province": "IL",
                "Administrative Zip / Postal Code": "62701",
                "Administrative Country": "USA",
                "Administrative Phone": "217-555-1234",
                "Administrative Fax": "217-555-5678",
                // Billing Address (addrType = 2)
                "Billing Street Address": "456 Billing Ave",
                "Billing City": "Springfield",
                "Billing State / Province": "IL",
                "Billing Zip / Postal Code": "62702",
                "Billing Country": "USA",
                "Billing Phone": "217-555-9876",
                "Billing Fax": "217-555-5432",
                // Prime Contact (optional - can be contact ID or email)
                "Prime Contact": "prime@example.com",
                // Membership/Contact information
                role_name: "Member",
                contact_email: "member@example.com",
                contact_first_name: "John",
                contact_family_name: "Doe",
                // Custom Attribute columns (CA: prefix)
                "CA: <S> Department": "Engineering",
                "CA: <B> Active": "true",
            },
        ];

        await SpreadSheetService.downloadExcel(
            templateData,
            "Organization_Import_Template.xlsx",
            "Organization Import Template",
        );
    }
}
